// src/components/PageHeader.tsx
import React, { useEffect, useRef, useState } from 'react';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { useTutorial } from '../context/TutorialContext';
import { PAGE_HEADER_HEIGHT } from '../constants/layout';

// Add interface for Safari on iOS which has the standalone property
interface SafariNavigator extends Navigator {
  standalone?: boolean;
}

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  primaryButton?: React.ReactNode;
  buttons?: React.ReactNode; // Alternative to primaryButton for more flexibility
}

const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, primaryButton, buttons }) => {
  const { openTutorial, hasTutorial, isOpen, closeTutorial } = useTutorial();
  const headerRef = useRef<HTMLDivElement>(null);
  const [isStandalone, setIsStandalone] = useState(false);

  // Add an effect to ensure the header stays fixed in standalone mode
  useEffect(() => {
    const checkStandalone = () => {
      const standalone = (window.matchMedia('(display-mode: standalone)').matches) || 
                         ((window.navigator as SafariNavigator).standalone) || 
                         document.referrer.includes('android-app://');
      
      setIsStandalone(standalone);
      
      if (headerRef.current && standalone) {
        // Force hardware acceleration and prevent the header from scrolling
        headerRef.current.style.transform = 'translateZ(0)';
        headerRef.current.style.willChange = 'transform';
        headerRef.current.style.position = 'fixed';
        headerRef.current.style.zIndex = '9999';
        
        // Don't manually add the padding here, we'll handle it with classes
      }
    };
    
    checkStandalone();
    
    // Also check when display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkStandalone();
    
    try {
      // Modern way
      mediaQuery.addEventListener('change', handleChange);
    } catch (e) {
      // Fallback for older browsers
      // @ts-ignore
      mediaQuery.addListener(handleChange);
    }
    
    return () => {
      try {
        mediaQuery.removeEventListener('change', handleChange);
      } catch (e) {
        // @ts-ignore
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // iOS in standalone mode needs its own adjustment
  const isIOSStandalone = isStandalone && (window.navigator as SafariNavigator).standalone;
  
  // Calculate appropriate padding class for iOS in standalone mode
  const paddingTopClass = isIOSStandalone ? 'pt-[env(safe-area-inset-top,0px)]' : '';
  
  // Adjust the inner content padding to ensure alignment with hamburger menu
  const contentPaddingClass = isIOSStandalone ? 'pl-10 lg:pl-0' : 'pl-10 lg:pl-0';
  
  // Adjust the header height to include the status bar in iOS standalone mode
  const headerHeightClass = isIOSStandalone ? 'min-h-[72px]' : 'h-[72px]';
  
  return (
    <div
      ref={headerRef}
      className={`
        fixed top-0 right-0 
        lg:right-0 
        left-0
        z-40
        bg-white dark:bg-gray-800 text-gray-800 dark:text-white
        shadow-md
        ${headerHeightClass}
        px-4
        ${paddingTopClass}
        flex justify-between items-center
        transition-all duration-200
        will-change-transform
      `}
      style={{
        position: 'fixed',
        WebkitOverflowScrolling: 'touch',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none'
      }}
    >
      <div className="flex flex-col justify-center shrink-0 max-w-[70%]">
        <div className="flex items-center flex-nowrap">
          <h1 className={`text-2xl font-bold ${contentPaddingClass} truncate`}>{title}</h1>
          {hasTutorial && (
            <button
              onClick={() => isOpen ? closeTutorial() : openTutorial()}
              className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shrink-0"
              aria-label={isOpen ? "Close page tutorial" : "Open page tutorial"}
            >
              <IoInformationCircleOutline size={18} />
            </button>
          )}
        </div>
        {subtitle && (
          <p className={`text-sm text-gray-500 dark:text-gray-400 ${contentPaddingClass}`}>{subtitle}</p>
        )}
      </div>
      {(primaryButton || buttons) && (
        <div className="flex items-center">
          {buttons || primaryButton}
        </div>
      )}
    </div>
  );
};

export default PageHeader;